import React, { useState } from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { SwitchAccount as SwitchAccountIcon, Undo as UndoIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_CONFIG } from '../config/authConfig';
import { isImpersonating, getImpersonatorInfo, restoreAdminSession, getSessionToken, getDisplayLogin } from '../utils/sessionManager';

/**
 * Режим «вход под сотрудником»: компактная плашка внизу по центру экрана.
 * Плашка fixed и в поток страницы не входит, поэтому раскладки высотой 100vh не получают лишней прокрутки.
 * Уведомления выводятся справа внизу и с ней не пересекаются; диалоги открываются поверх плашки.
 */
export const ImpersonationBanner: React.FC = () => {
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating()) {
    return null;
  }

  const adminInfo = getImpersonatorInfo();
  const targetUsername = getDisplayLogin();
  const targetFullName = localStorage.getItem('fullName') || targetUsername;
  const adminName = adminInfo?.adminFullName || adminInfo?.adminUsername || 'администратора';

  const handleExitImpersonation = async () => {
    setExiting(true);
    const token = getSessionToken();

    try {
      if (token) {
        await axios.post(`${API_CONFIG.AUTH_V2_URL}/impersonate/exit`, {
          impersonatedToken: token
        });
      }
    } catch (err) {
      console.warn('Ошибка при отправке сигнала выхода из имперсонации:', err);
    } finally {
      // Администратор возвращается в раздел, из которого входил под сотрудником
      window.location.href = restoreAdminSession() || '/';
    }
  };

  return (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: 16,
        transform: 'translateX(-50%)',
        // Выше боковых панелей (1200), ниже диалогов (1300)
        zIndex: 1250,
        maxWidth: 'calc(100vw - 32px)',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 0.75,
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)'
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: '8px',
          bgcolor: '#fffbeb',
          color: '#b45309',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <SwitchAccountIcon sx={{ fontSize: 18 }} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.2 }}>
          Вход под сотрудником
        </Typography>
        <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.35 }}>
          {targetFullName}
          {targetFullName !== targetUsername && (
            <Box component="span" sx={{ color: '#64748b', fontWeight: 500 }}> · {targetUsername}</Box>
          )}
        </Typography>
      </Box>

      <Tooltip title={`Вернуться в аккаунт ${adminName}`}>
        <span>
          <Button
            size="small"
            variant="outlined"
            onClick={handleExitImpersonation}
            disabled={exiting}
            startIcon={exiting ? <CircularProgress size={14} color="inherit" /> : <UndoIcon sx={{ fontSize: 16 }} />}
            sx={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
              height: 32,
              px: 1.5,
              borderRadius: '8px',
              borderColor: '#e2e8f0',
              color: '#1e293b',
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
            }}
          >
            {exiting ? 'Возврат...' : 'Вернуться'}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

export default ImpersonationBanner;
